"use client";

import Link from "next/link";
import { useState } from "react";
import { ArrowLeft, X, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import PageHero from "@/components/landing/PageHero";
import { AnimatedSection } from "@/hooks/useScrollAnimation";
import { Markdown } from "@/components/common/Markdown";
import type { NewsEntry } from "@/lib/cms";

const newsFoodbank = "/assets/news-foodbank.png";
const newsNetworking = "/assets/news-networking.png";
const newsAshleyLaughing = "/assets/news-ashley-laughing.png";
const newsFamily = "/assets/news-family.png";
const newsRibbonCuttings = "/assets/news-ribbon-cuttings.png";

export const galleryImages = [
  { src: newsFoodbank, alt: "Volunteering at Tarrant Area Food Bank" },
  { src: newsNetworking, alt: "Networking with local business community" },
  { src: newsAshleyLaughing, alt: "Ashley Stuhr" },
  { src: newsFamily, alt: "The Stuhr family" },
  { src: newsRibbonCuttings, alt: "Community ribbon cutting events" },
];

type Props = { article: NewsEntry | null };

const NewsArticle = ({ article }: Props) => {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const openLightbox = (index: number) => setLightboxIndex(index);
  const closeLightbox = () => setLightboxIndex(null);
  const goNext = () =>
    setLightboxIndex((prev) => (prev !== null ? (prev + 1) % galleryImages.length : null));
  const goPrev = () =>
    setLightboxIndex((prev) =>
      prev !== null ? (prev - 1 + galleryImages.length) % galleryImages.length : null,
    );

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="font-body text-base text-foreground/60">Article not found.</p>
      </div>
    );
  }

  const subtitleParts = [article.publication, article.date].filter(Boolean);

  return (
    <div className="min-h-screen">
      <Navbar />

      <PageHero
        title={article.title}
        subtitle={subtitleParts.join(" • ")}
        backgroundImage={article.heroImage || "/assets/hero-blog.jpg"}
      />

      <section className="py-16 px-6 lg:px-12 bg-white">
        <AnimatedSection className="max-w-[760px] mx-auto">
          <Link href="/news">
            <Button
              variant="ghost"
              className="text-accent-blue hover:text-accent-blue/80 mb-8 p-0 h-auto font-button text-sm"
            >
              <ArrowLeft className="w-4 h-4 mr-1" /> Back to News
            </Button>
          </Link>

          {article.excerpt && (
            <p className="font-body text-base text-foreground/80 leading-relaxed mb-10">
              {article.excerpt}
            </p>
          )}

          <Markdown className="prose prose-lg max-w-none font-body">{article.body}</Markdown>

          {/* Photo Gallery */}
          <div className="mt-14 mb-12">
            <h2 className="font-heading font-bold text-xl text-foreground mb-6">Photos</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {galleryImages.map((img, i) => (
                <button
                  key={i}
                  onClick={() => openLightbox(i)}
                  className={`overflow-hidden rounded-xl cursor-pointer group ${
                    i === 0 ? "col-span-2 row-span-2" : ""
                  }`}
                >
                  <img
                    src={img.src}
                    alt={img.alt}
                    className={`w-full object-cover object-top transition-transform duration-300 group-hover:scale-105 ${
                      i === 0 ? "h-[320px]" : "h-[155px]"
                    }`}
                  />
                </button>
              ))}
            </div>
          </div>

          <div className="pt-8 border-t border-foreground/10 text-center">
            {article.publication && (
              <p className="font-body text-sm text-foreground/60 mb-4">
                Originally published on{" "}
                <a
                  href="https://voyagedallas.com/interview/life-values-legacy-our-chat-with-ashley-stuhr-of-fort-worth-highlight/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-accent-blue hover:underline"
                >
                  {article.publication}
                </a>
              </p>
            )}
            <Link href="/contact">
              <Button className="bg-accent-blue hover:bg-accent-blue/90 text-white rounded-full px-6 py-3 font-button text-sm cta-hover">
                Let&apos;s Talk Healthcare
              </Button>
            </Link>
          </div>
        </AnimatedSection>
      </section>

      {/* Lightbox */}
      {lightboxIndex !== null && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center"
          onClick={closeLightbox}
        >
          <button
            onClick={(e) => {
              e.stopPropagation();
              closeLightbox();
            }}
            className="absolute top-6 right-6 text-white/80 hover:text-white transition-colors z-10"
            aria-label="Close lightbox"
          >
            <X className="w-8 h-8" />
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation();
              goPrev();
            }}
            className="absolute left-4 lg:left-8 text-white/60 hover:text-white transition-colors z-10"
            aria-label="Previous image"
          >
            <ChevronLeft className="w-10 h-10" />
          </button>

          <img
            src={galleryImages[lightboxIndex].src}
            alt={galleryImages[lightboxIndex].alt}
            className="max-h-[85vh] max-w-[90vw] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />

          <button
            onClick={(e) => {
              e.stopPropagation();
              goNext();
            }}
            className="absolute right-4 lg:right-8 text-white/60 hover:text-white transition-colors z-10"
            aria-label="Next image"
          >
            <ChevronRight className="w-10 h-10" />
          </button>

          <div className="absolute bottom-6 text-white/60 font-body text-sm">
            {lightboxIndex + 1} / {galleryImages.length}
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default NewsArticle;
